import React, { useState } from 'react';

// Visitor & Tourist Visa Step 1: Visa Type & Purpose Selection
export const VisitorStep1VisaTypeSelection = ({ formData, setFormData, onNext }) => {
  const [selectedVisaType, setSelectedVisaType] = useState(formData.visaType || '');
  const [purposeOfVisit, setPurposeOfVisit] = useState(formData.purposeOfVisit || '');

  const visaTypes = [
    {
      id: 'b1',
      title: 'B-1 Business Visitor',
      description: 'Temporary business activities, meetings, conferences',
      icon: '💼',
      purposes: [
        'Business meetings and conferences',
        'Contract negotiations',
        'Trade shows and exhibitions',
        'Training programs',
        'Board meetings'
      ],
      duration: 'Up to 6 months',
      requirements: ['Valid passport', 'Business invitation letter', 'Proof of employment']
    },
    {
      id: 'b2',
      title: 'B-2 Tourism/Pleasure',
      description: 'Tourism, vacation, visiting friends and relatives',
      icon: '🏖️',
      purposes: [
        'Tourism and sightseeing',
        'Visiting friends and family',
        'Medical treatment',
        'Recreation and hobbies',
        'Amateur sports participation'
      ],
      duration: 'Up to 6 months',
      requirements: ['Valid passport', 'Proof of ties to home country', 'Financial support evidence']
    },
    {
      id: 'b1b2',
      title: 'B-1/B-2 Combined',
      description: 'Mixed business and tourism purposes',
      icon: '🌍',
      purposes: [
        'Business meetings + tourism',
        'Conference attendance + vacation',
        'Medical treatment + family visit',
        'Training + sightseeing'
      ],
      duration: 'Up to 6 months',
      requirements: ['Valid passport', 'Documentation for both purposes', 'Clear itinerary']
    },
    {
      id: 'esta',
      title: 'ESTA (Visa Waiver)',
      description: 'Electronic System for Travel Authorization',
      icon: '📱',
      purposes: [
        'Short-term business (90 days max)',
        'Tourism (90 days max)',
        'Transit through US'
      ],
      duration: 'Up to 90 days',
      requirements: ['Eligible country passport', 'Return/onward ticket', 'Online ESTA approval'],
      note: 'For citizens of Visa Waiver Program countries only'
    }
  ];

  const handleVisaTypeSelection = (visaId) => {
    setSelectedVisaType(visaId);
    setFormData({ 
      ...formData, 
      visaType: visaId,
      visaDetails: visaTypes.find(v => v.id === visaId)
    });
  };

  const handlePurposeChange = (purpose) => {
    setPurposeOfVisit(purpose);
    setFormData({ ...formData, purposeOfVisit: purpose });
  };

  const handleContinue = () => {
    if (!selectedVisaType) {
      setSelectedVisaType('b2'); // Default to B-2 tourism
      setFormData({ 
        ...formData, 
        visaType: 'b2',
        visaDetails: visaTypes.find(v => v.id === 'b2')
      });
    }
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Select Your Visa Type & Purpose</h3>
        <p className="text-gray-400 text-lg">Choose the visa category that best fits your travel purpose</p>
      </div>

      {/* Visa Type Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {visaTypes.map((visa) => (
          <div
            key={visa.id}
            onClick={() => handleVisaTypeSelection(visa.id)}
            className={`p-6 rounded-lg border-2 transition-all cursor-pointer ${
              selectedVisaType === visa.id
                ? 'border-blue-500 bg-blue-900/20'
                : 'border-gray-700 bg-gray-800 hover:border-blue-400'
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className={`w-6 h-6 rounded-full border-2 mr-4 flex items-center justify-center ${
                  selectedVisaType === visa.id ? 'border-blue-500 bg-blue-500' : 'border-gray-500'
                }`}>
                  {selectedVisaType === visa.id && <span className="text-white text-sm">✓</span>}
                </div>
                <div className="flex items-center">
                  <span className="text-3xl mr-3">{visa.icon}</span>
                  <div>
                    <h5 className="text-xl font-semibold text-white">{visa.title}</h5>
                    <p className="text-gray-300 mt-1">{visa.description}</p>
                  </div>
                </div>
              </div>
            </div>

            {visa.note && (
              <div className="mb-4 p-3 bg-yellow-900/20 border border-yellow-400 rounded">
                <p className="text-yellow-300 text-sm">{visa.note}</p>
              </div>
            )}

            <div className="ml-10">
              <div className="mb-3">
                <h6 className="text-blue-300 font-medium mb-2">Common Purposes:</h6>
                <ul className="space-y-1">
                  {visa.purposes.map((purpose, index) => (
                    <li key={index} className="text-gray-400 text-sm flex items-center">
                      <span className="text-blue-400 mr-2">•</span>
                      {purpose}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-3">
                <h6 className="text-green-300 font-medium mb-1">Duration:</h6>
                <p className="text-green-200 text-sm">{visa.duration}</p>
              </div>

              <div>
                <h6 className="text-purple-300 font-medium mb-1">Key Requirements:</h6>
                <ul className="text-purple-200 text-sm space-y-1">
                  {visa.requirements.map((req, index) => (
                    <li key={index}>• {req}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Purpose Details */}
      {selectedVisaType && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">Specify Your Purpose of Visit</h4>
          <textarea
            value={purposeOfVisit}
            onChange={(e) => handlePurposeChange(e.target.value)}
            placeholder="Please describe the specific purpose of your visit, dates, locations, and any relevant details..."
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500 h-32"
          />
          <p className="text-gray-400 text-sm mt-2">
            Be specific about your travel plans. This helps in preparing the strongest possible application.
          </p>
        </div>
      )}

      {/* Information Notice */}
      <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
        <h4 className="text-blue-300 font-semibold mb-4">📋 Important Information</h4>
        <ul className="text-blue-200 text-sm space-y-2">
          <li>• Your purpose of visit must be legitimate and clearly documented</li>
          <li>• B-1/B-2 visas are for temporary visits only - you cannot work in the US</li>
          <li>• You must demonstrate strong ties to your home country</li>
          <li>• Provide honest and accurate information throughout the process</li>
          <li>• ESTA applications must be submitted at least 72 hours before travel</li>
        </ul>
      </div>

      {/* Flexibility Notice */}
      <div className="bg-emerald-900/20 border border-emerald-400 p-4 rounded-lg">
        <h4 className="text-emerald-300 font-semibold mb-2">🚀 Getting Started</h4>
        <p className="text-emerald-200 text-sm">
          If you're not sure which visa type applies, don't worry! You can proceed and the system will guide you. B-2 Tourism is selected by default.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-end">
        <button
          onClick={handleContinue}
          className="px-6 py-3 rounded-lg font-medium transition-colors bg-blue-500 text-white hover:bg-blue-600"
        >
          Continue to Personal Details →
        </button>
      </div>
    </div>
  );
};

// Visitor & Tourist Visa Step 2: Personal Information & Travel History
export const VisitorStep2PersonalDetails = ({ formData, setFormData, onNext, onBack }) => {
  const [personalDetails, setPersonalDetails] = useState(formData.personalDetails || {});
  const [travelHistory, setTravelHistory] = useState(formData.travelHistory || {});

  const handlePersonalChange = (field, value) => {
    const updated = { ...personalDetails, [field]: value };
    setPersonalDetails(updated);
    setFormData({ ...formData, personalDetails: updated });
  };

  const handleTravelChange = (field, value) => {
    const updated = { ...travelHistory, [field]: value };
    setTravelHistory(updated);
    setFormData({ ...formData, travelHistory: updated });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Personal Information & Travel History</h3>
        <p className="text-gray-400 text-lg">Provide your personal details and travel background</p>
      </div>

      {/* Personal Information */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">👤 Personal Information</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2">Full Name (as on passport)</label>
            <input
              type="text"
              value={personalDetails.fullName || ''}
              onChange={(e) => handlePersonalChange('fullName', e.target.value)}
              placeholder="First Middle Last Name"
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Date of Birth</label>
            <input
              type="date"
              value={personalDetails.dateOfBirth || ''}
              onChange={(e) => handlePersonalChange('dateOfBirth', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Country of Birth</label>
            <input
              type="text"
              value={personalDetails.countryOfBirth || ''}
              onChange={(e) => handlePersonalChange('countryOfBirth', e.target.value)}
              placeholder="Country where you were born"
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Country of Citizenship</label>
            <input
              type="text"
              value={personalDetails.citizenship || ''}
              onChange={(e) => handlePersonalChange('citizenship', e.target.value)}
              placeholder="Country of your passport"
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Passport Number</label>
            <input
              type="text"
              value={personalDetails.passportNumber || ''}
              onChange={(e) => handlePersonalChange('passportNumber', e.target.value)}
              placeholder="Passport number"
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Passport Expiration Date</label>
            <input
              type="date"
              value={personalDetails.passportExpiration || ''}
              onChange={(e) => handlePersonalChange('passportExpiration', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-gray-300 mb-2">Current Address</label>
          <textarea
            value={personalDetails.currentAddress || ''}
            onChange={(e) => handlePersonalChange('currentAddress', e.target.value)}
            placeholder="Your current residential address"
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500 h-20"
          />
        </div>
      </div>

      {/* Travel History */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">✈️ Travel History</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2">Previous US Visits</label>
            <select
              value={travelHistory.previousUSVisits || ''}
              onChange={(e) => handleTravelChange('previousUSVisits', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">Select previous US visits</option>
              <option value="none">Never visited the US</option>
              <option value="once">Visited once before</option>
              <option value="multiple">Multiple visits</option>
              <option value="frequent">Frequent traveler</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Last US Visit Date</label>
            <input
              type="date"
              value={travelHistory.lastUSVisit || ''}
              onChange={(e) => handleTravelChange('lastUSVisit', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">International Travel Experience</label>
            <select
              value={travelHistory.internationalTravel || ''}
              onChange={(e) => handleTravelChange('internationalTravel', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">Select travel experience</option>
              <option value="limited">Limited international travel</option>
              <option value="moderate">Moderate traveler</option>
              <option value="extensive">Extensive travel history</option>
              <option value="business">Frequent business traveler</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Visa Refusals</label>
            <select
              value={travelHistory.visaRefusals || ''}
              onChange={(e) => handleTravelChange('visaRefusals', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">Select visa refusal history</option>
              <option value="none">No visa refusals</option>
              <option value="us-refused">US visa refused before</option>
              <option value="other-refused">Other country visa refused</option>
              <option value="multiple-refused">Multiple refusals</option>
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-gray-300 mb-2">Travel History Details</label>
          <textarea
            value={travelHistory.travelDetails || ''}
            onChange={(e) => handleTravelChange('travelDetails', e.target.value)}
            placeholder="Describe your international travel history, including countries visited, purposes, and any relevant details..."
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500 h-24"
          />
        </div>
      </div>

      {/* Planned Trip Details */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📅 Planned Trip Details</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2">Planned Arrival Date</label>
            <input
              type="date"
              value={personalDetails.plannedArrival || ''}
              onChange={(e) => handlePersonalChange('plannedArrival', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Planned Departure Date</label>
            <input
              type="date"
              value={personalDetails.plannedDeparture || ''}
              onChange={(e) => handlePersonalChange('plannedDeparture', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Duration of Stay</label>
            <select
              value={personalDetails.duration || ''}
              onChange={(e) => handlePersonalChange('duration', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">Select duration</option>
              <option value="1-2weeks">1-2 weeks</option>
              <option value="3-4weeks">3-4 weeks</option>
              <option value="1-2months">1-2 months</option>
              <option value="3-6months">3-6 months</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Primary Destination</label>
            <input
              type="text"
              value={personalDetails.primaryDestination || ''}
              onChange={(e) => handlePersonalChange('primaryDestination', e.target.value)}
              placeholder="City/State you'll primarily visit"
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Flexibility Notice */}
      <div className="bg-green-900/20 border border-green-400 p-4 rounded-lg">
        <h4 className="text-green-300 font-semibold mb-2">✅ Flexible Information</h4>
        <p className="text-green-200 text-sm">
          You can proceed even if some details are incomplete. The system will work with whatever information you provide and you can update details later.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Visa Type Selection
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium transition-colors bg-blue-500 text-white hover:bg-blue-600"
        >
          Continue to Document Upload →
        </button>
      </div>
    </div>
  );
};

// Visitor & Tourist Visa Step 3: Document Upload
export const VisitorStep3DocumentUpload = ({ formData, setFormData, onNext, onBack }) => {
  const [documents, setDocuments] = useState(formData.documents || []);
  const [uploadCategory, setUploadCategory] = useState('passport-identity');

  const documentCategories = [
    {
      id: 'passport-identity',
      title: 'Passport & Identity Documents',
      description: 'Primary identification and passport documentation',
      icon: '🛂',
      examples: ['Valid passport', 'Previous passports', 'National ID card', 'Birth certificate']
    },
    {
      id: 'financial-support',
      title: 'Financial Support Evidence',
      description: 'Proof of financial ability to support your trip',
      icon: '💰',
      examples: ['Bank statements', 'Pay stubs', 'Employment letter', 'Sponsorship letter', 'Tax returns']
    },
    {
      id: 'travel-plans',
      title: 'Travel Plans & Itinerary',
      description: 'Documentation of your planned trip',
      icon: '📋',
      examples: ['Flight itinerary', 'Hotel reservations', 'Tour bookings', 'Travel insurance', 'Return ticket']
    },
    {
      id: 'ties-to-home',
      title: 'Ties to Home Country',
      description: 'Evidence of your connections to home country',
      icon: '🏠',
      examples: ['Property ownership', 'Family ties', 'Job letter', 'Business ownership', 'School enrollment']
    },
    {
      id: 'invitation-support',
      title: 'Invitation & Support Letters',
      description: 'Letters from US contacts or sponsors',
      icon: '📝',
      examples: ['Invitation letter', 'Host\'s documents', 'Family relationship proof', 'Business invitation']
    },
    {
      id: 'previous-travel',
      title: 'Previous Travel Documentation',
      description: 'History of your international travel',
      icon: '✈️',
      examples: ['Previous visas', 'Entry/exit stamps', 'Travel photos', 'Prior trip documentation']
    }
  ];

  const requiredDocumentsByVisaType = {
    'b1': [
      'Valid passport',
      'Business invitation letter',
      'Employment verification',
      'Financial support evidence',
      'Travel itinerary'
    ],
    'b2': [
      'Valid passport',
      'Financial support evidence', 
      'Travel plans',
      'Ties to home country',
      'Return flight booking'
    ],
    'b1b2': [
      'Valid passport',
      'Business invitation/travel plans',
      'Financial support evidence',
      'Employment verification',
      'Ties to home country'
    ],
    'esta': [
      'Valid passport',
      'Return/onward ticket',
      'Travel itinerary',
      'Financial support evidence'
    ]
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    const newDocuments = [];
    
    for (const file of files) {
      try {
        // Convert file to base64 for OCR analysis
        const fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        console.log(`🔍 Analyzing document: ${file.name} (${file.type}, ${file.size} bytes)`);

        // Call backend for actual document analysis with Google Vision OCR
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const response = await fetch(`${backendUrl}/api/ai/analyze-uploaded-document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            document_file: fileData,
            document_name: file.name,
            document_type: uploadCategory,
            case_context: {
              case_type: 'Visitor/Tourist Visa Application',
              agent_type: 'visitor-tourist',
              applicant: formData.personalInfo?.fullName || 'Visitor/Tourist Applicant',
              visa_type: formData.visaType || 'b1-b2'
            }
          })
        });

        let documentAnalysis;
        if (response.ok) {
          documentAnalysis = await response.json();
          console.log(`✅ Document analysis successful for ${file.name}:`, documentAnalysis);
        } else {
          const errorText = await response.text();
          console.error(`❌ Document analysis failed for ${file.name}:`, response.status, errorText);
          documentAnalysis = {
            document_type: uploadCategory,
            content_description: `Document analysis failed: ${errorText}`,
            case_relevance: "Manual review required to determine relevance",
            exhibit_description: `Exhibit: ${file.name} requiring detailed manual review`,
            immigration_value: "Medium",
            analysis_error: `Analysis failed: ${errorText}`
          };
        }

        // Create document object with OCR analysis results
        const newDoc = {
          id: `doc_${Date.now()}_${Math.floor(performance.now() * 1000)}`,
          name: file.name,
          category: uploadCategory,
          file: file,
          timestamp: new Date().toISOString(),
          size: file.size,
          // Add OCR analysis results
          analysis: documentAnalysis,
          content_description: documentAnalysis.content_description,
          parties_mentioned: documentAnalysis.parties_mentioned,
          key_dates: documentAnalysis.key_dates,
          case_relevance: documentAnalysis.case_relevance,
          exhibit_description: documentAnalysis.exhibit_description,
          immigration_value: documentAnalysis.immigration_value,
          red_flags: documentAnalysis.red_flags,
          authenticity_indicators: documentAnalysis.authenticity_indicators,
          extracted_text: documentAnalysis.extracted_text
        };

        newDocuments.push(newDoc);
        
      } catch (error) {
        console.error('Document analysis error:', error);
        
        // Add document with error indication
        const newDoc = {
          id: `doc_${Date.now()}_${Math.floor(performance.now() * 1000)}`,
          name: file.name,
          category: uploadCategory,
          file: file,
          timestamp: new Date().toISOString(),
          size: file.size,
          analysis_error: "Analysis failed - manual review required",
          content_description: `Document analysis failed for ${file.name}`,
          case_relevance: "Manual review required",
          exhibit_description: `Exhibit: ${file.name} (manual review required)`,
          immigration_value: "Unknown"
        };
        
        newDocuments.push(newDoc);
      }
    }

    const updatedDocuments = [...documents, ...newDocuments];
    setDocuments(updatedDocuments);
    setFormData({ ...formData, documents: updatedDocuments });
  };

  const removeDocument = (docId) => {
    const updatedDocuments = documents.filter(doc => doc.id !== docId);
    setDocuments(updatedDocuments);
    setFormData({ ...formData, documents: updatedDocuments });
  };

  const getRequiredDocuments = () => {
    return requiredDocumentsByVisaType[formData.visaType] || [];
  };

  const getDocumentCompleteness = () => {
    const required = getRequiredDocuments();
    const uploaded = documents.map(doc => doc.name.toLowerCase());
    
    const found = required.filter(reqDoc => 
      uploaded.some(uploadedDoc => 
        uploadedDoc.includes(reqDoc.toLowerCase().replace(/\s+/g, '')) ||
        reqDoc.toLowerCase().replace(/\s+/g, '').includes(uploadedDoc.replace(/\s+/g, ''))
      )
    );

    return {
      found: found.length,
      total: required.length,
      percentage: required.length > 0 ? (found.length / required.length) * 100 : 0,
      missing: required.filter(reqDoc => 
        !uploaded.some(uploadedDoc => 
          uploadedDoc.includes(reqDoc.toLowerCase().replace(/\s+/g, '')) ||
          reqDoc.toLowerCase().replace(/\s+/g, '').includes(uploadedDoc.replace(/\s+/g, ''))
        )
      )
    };
  };

  const completeness = getDocumentCompleteness();

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Upload Supporting Documents</h3>
        <p className="text-gray-400 text-lg">Upload documents to support your visa application</p>
      </div>

      {/* Document Requirements */}
      <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
        <h4 className="text-blue-300 font-semibold mb-4">📋 Required Documents for {formData.visaType?.toUpperCase()} Visa</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {getRequiredDocuments().map((doc, index) => (
            <div key={index} className="flex items-center text-blue-200 text-sm">
              <span className="text-blue-400 mr-2">•</span>
              {doc}
            </div>
          ))}
        </div>
      </div>

      {/* Document Category Selection */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📂 Select Document Category</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documentCategories.map((category) => (
            <div
              key={category.id}
              onClick={() => setUploadCategory(category.id)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                uploadCategory === category.id
                  ? 'border-blue-500 bg-blue-900/20'
                  : 'border-gray-600 bg-gray-700 hover:border-blue-400'
              }`}
            >
              <div className="text-center">
                <div className="text-3xl mb-2">{category.icon}</div>
                <h5 className="text-white font-medium mb-1">{category.title}</h5>
                <p className="text-gray-400 text-xs">{category.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* File Upload */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📤 Upload Documents</h4>
        
        <div className="mb-4 p-4 bg-gray-700 rounded">
          <div className="flex items-center mb-2">
            <span className="text-2xl mr-3">
              {documentCategories.find(c => c.id === uploadCategory)?.icon}
            </span>
            <h5 className="text-white font-medium">
              {documentCategories.find(c => c.id === uploadCategory)?.title}
            </h5>
          </div>
          <p className="text-gray-300 text-sm mb-2">Examples:</p>
          <ul className="text-gray-400 text-sm space-y-1">
            {documentCategories.find(c => c.id === uploadCategory)?.examples.map((example, index) => (
              <li key={index}>• {example}</li>
            ))}
          </ul>
        </div>

        <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center">
          <input
            type="file"
            multiple
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            <div className="text-6xl text-gray-500 mb-4">📁</div>
            <h4 className="text-xl text-white mb-2">Drop files here or click to upload</h4>
            <p className="text-gray-400 text-sm">
              Supported formats: PDF, Word, Images, Text files
            </p>
          </label>
        </div>
      </div>

      {/* Document Completeness */}
      {documents.length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-xl font-semibold text-white">📋 Document Completeness</h4>
            <div className={`text-2xl font-bold ${
              completeness.percentage >= 80 ? 'text-emerald-400' :
              completeness.percentage >= 50 ? 'text-yellow-400' :
              'text-red-400'
            }`}>
              {Math.round(completeness.percentage)}%
            </div>
          </div>
          
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-300 mb-1">
              <span>Required Documents Found</span>
              <span>{completeness.found}/{completeness.total}</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  completeness.percentage >= 80 ? 'bg-emerald-400' :
                  completeness.percentage >= 50 ? 'bg-yellow-400' :
                  'bg-red-400'
                }`}
                style={{ width: `${completeness.percentage}%` }}
              ></div>
            </div>
          </div>

          {completeness.missing.length > 0 && (
            <div className="bg-yellow-900/20 border border-yellow-400 p-4 rounded">
              <h5 className="text-yellow-300 font-medium mb-2">Missing Documents:</h5>
              <ul className="text-yellow-200 text-sm space-y-1">
                {completeness.missing.map((doc, index) => (
                  <li key={index}>• {doc}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Uploaded Documents */}
      {documents.length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">📋 Uploaded Documents ({documents.length})</h4>
          <div className="space-y-3">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-4 bg-gray-700 rounded">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">
                    {documentCategories.find(c => c.id === doc.category)?.icon || '📄'}
                  </span>
                  <div>
                    <h5 className="text-white font-medium">{doc.name}</h5>
                    <p className="text-gray-400 text-sm">
                      {documentCategories.find(c => c.id === doc.category)?.title} • 
                      {(doc.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => removeDocument(doc.id)}
                  className="text-red-400 hover:text-red-300 p-2"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Flexibility Notice */}
      <div className="bg-blue-900/20 border border-blue-400 p-4 rounded-lg">
        <h4 className="text-blue-300 font-semibold mb-2">📌 Flexible Process</h4>
        <p className="text-blue-200 text-sm">
          You can proceed to the next step even without uploading all documents now. You can always return to add more documents later in the process.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Personal Details
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium transition-colors bg-blue-500 text-white hover:bg-blue-600"
        >
          Continue to Application Analysis →
        </button>
      </div>
    </div>
  );
};